import { useCallback } from 'react'
import useSearchParamsEx from '@hook/useSearchParamsEx'
import { useQuery, useMutation } from '@tanstack/react-query'
import * as actions from '$actions'
import useCheckableItems from '@hook/useCheckableItems'
import { toast } from 'react-toastify'
import cn from 'clsx'
import IndeterminateLoadingBar from '@components/shared/IndeterminateLoadingBar'
import Checkbox from '$components/Forms/Checkbox'
import { BsSortDown, BsSortUp } from 'react-icons/bs'
import { MdDeleteSweep, MdDelete } from 'react-icons/md'

const changeOrderDirection = prev =>
    prev === 'asc' ? 'desc' : 'asc'

const BenchmarksTable = () => {
    
    const [searchParams, updateSearchParams] = useSearchParamsEx({ q: '' })
    
    const {
        data: benchmarks,
        error: benchmarksError,
        isLoading: benchmarksIsLoading,
        refetch: refetchBenchmarks,
    } = useQuery({
        queryKey: [
            'benchmarks',
            searchParams.get('topic'),
            searchParams.get('offset'),
            searchParams.get('limit'),
            searchParams.get('orderBy'),
            searchParams.get('orderDirection'),
        ],
        queryFn: () => actions.fetchBenchmarks(
            searchParams.get('topic'),
            searchParams.get('offset'),
            searchParams.get('limit'),
            searchParams.get('orderBy'),
            searchParams.get('orderDirection'),
        ).catch(e => {
            console.error('@todo show fetchBenchmarks error', e)
            toast.error('Failed to fetch benchmarks')
            return null
        }),
        retry: 0,
        keepPreviousData: true,
    })
    
    const { mutate: deleteBenchmarksByIds } = useMutation({
        queryKey: ['deleteBenchmarksByIds', refetchBenchmarks],
        mutationFn: ids => actions.deleteBenchmarks(ids),
        onSuccess: () => {
            refetchBenchmarks()
            toast.success('Benchmarks deleted')
        },
        onError: e => {
            console.error('Error deleting benchmarks', e)
            toast.error('Failed to delete benchmarks')
        },
    })
    
    const {
        checked,
        anyChecked,
        allChecked,
        someChecked,
        hasChecked,
        setChecked,
        toggleChecked,
        toggleAllChecked,
    } = useCheckableItems(benchmarks || [])
    
    const updateSort = useCallback(key => updateSearchParams({
        orderBy: key,
        orderDirection: searchParams.get('orderBy') === key
            ? changeOrderDirection(searchParams.get('orderDirection'))
            : searchParams.get('orderDirection') ?? 'desc',
    }), [searchParams.get('orderBy'), searchParams.get('orderDirection')])
    
    const onDeleteClick = useCallback(async id => {
        
        await deleteBenchmarksByIds([id])
        
        setChecked(id, false)
        
    }, [checked])
    
    const onDeleteCheckedClick = useCallback(async () => {
        
        await deleteBenchmarksByIds(checked)
        
        setChecked(checked, false)
        
    }, [checked])
    
    //deleteBenchmarks
    
    if (benchmarksError) return console.error(benchmarksError) || (
        <div className="BenchmarksTable flex flex-col justify-center items-center gap-4">
            <div className="text-center">
                <p>Failed to fetch benchmarks</p>
                <div><pre><code>{JSON.stringify(benchmarksError, null, 4)}</code></pre></div>
            </div>
        </div>
    )
    
    return (<>
        
        <IndeterminateLoadingBar
            className="!h-1 rounded"
            isLoading={benchmarksIsLoading}
            colorClass="bg-base-200/50"
            altColorClass="bg-base-300/50" />
        
        <table className="BenchmarksTable table-auto table-sm table-sticky-headers">
            
            <thead>
                <tr>
                    <th>
                        <Checkbox
                            checked={allChecked}
                            indeterminate={someChecked}
                            onChange={toggleAllChecked} />
                    </th>
                    <th>User</th>
                    <th>Organization</th>
                    <th>
                        <div
                            className={cn('cursor-pointer', {
                                'text-accent': searchParams.get('orderBy') === 'topic',
                            })}
                            onClick={() => updateSort('topic')}>
                            <span>Topic</span>
                            {searchParams.get('orderDirection') === 'desc' ? <BsSortDown /> : <BsSortUp />}
                        </div>
                    </th>
                    <th>Start</th>
                    <th>End</th>
                    <th>Diff</th>
                    <th>
                        <div
                            className={cn('cursor-pointer', {
                                'text-accent': searchParams.get('orderBy') === 'seconds',
                            })}
                            onClick={() => updateSort('seconds')}>
                            <span>Seconds</span>
                            {searchParams.get('orderDirection') === 'desc' ? <BsSortDown /> : <BsSortUp />}
                        </div>
                    </th>
                    <th>Milliseconds</th>
                    <th>
                        <div className="flex justify-center items-center content-center gap-2">
                            <button
                                className="btn btn-sm btn-square btn-error"
                                disabled={!anyChecked}
                                onClick={onDeleteCheckedClick}>
                                <MdDeleteSweep className="!ml-0" />
                            </button>
                        </div>
                    </th>
                </tr>
            </thead>
            
            <tbody>
                {benchmarks?.map(it => (
                    <tr key={`BenchmarksTable-benchmark-${it.id}`}>
                        <td>
                            <Checkbox
                                checked={hasChecked(it.id)}
                                onChange={() => toggleChecked(it.id)} />
                        </td>
                        <td>{it.createdBy?.email ?? 'N/A'}</td>
                        <td>{it.organization?.name ?? 'N/A'}</td>
                        <td>{it.topic}</td>
                        <td>{it.start}</td>
                        <td>{it.end}</td>
                        <td>{it.diff}</td>
                        <td>{it.seconds}</td>
                        <td>{it.milliseconds}</td>
                        <td>
                            <div className="flex justify-center items-center content-center gap-2">
                                <button
                                    className="btn btn-sm btn-square btn-error"
                                    onClick={() => onDeleteClick(it.id)}>
                                    <MdDelete />
                                </button>
                            </div>
                        </td>
                    </tr>
                ))}
            </tbody>
        
        </table>
    
    </>)
    
}

export default BenchmarksTable
